/*
  component-slider-vertical-static.js
  ----------------------------------
  Drop‑in replacement for the original `component-slider-vertical.js` when you
  want the section to render **statically** – i.e. without IntersectionObserver‑
  driven “on‑scroll” effects or parallax transforms – while keeping exactly the
  same markup and visual appearance.

  👉 1. Upload this file to **assets/** in your theme.
  👉 2. In the section template replace
       <script src="{{ 'component-slider-vertical.js' | asset_url }}" defer></script>
       with
       <script src="{{ 'component-slider-vertical-static.js' | asset_url }}" defer></script>

  No further changes required.  
*/

// very small debounce helper – keeps our resize handler cheap
function _debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (typeof SliderVerticalStatic !== "function") {
  class SliderVerticalStatic extends HTMLElement {
    constructor() {
      super();

      /*
        ──────────────────────────────────────────────────────────
        1. Duplicate headings (the original script did that too)
        ──────────────────────────────────────────────────────────
      */
      const headingsHolder = this.querySelector(".vertical-slider-headings");
      if (headingsHolder) {
        this.querySelectorAll(".slide-heading").forEach((heading) => {
          const clone = heading.cloneNode(true);
          clone.style.display = "flex"; // always visible
          headingsHolder.appendChild(clone);
        });
      }

      /*
        ──────────────────────────────────────────────────────────
        2. Mark the first slide active → legacy CSS keeps layout
        ──────────────────────────────────────────────────────────
      */
      const firstSlide = this.querySelector(".slide");
      if (firstSlide) firstSlide.classList.add("active");

      /*
        ──────────────────────────────────────────────────────────
        3. Remove any parallax attributes that 3rd‑party libs use
        ──────────────────────────────────────────────────────────
      */
      this.querySelectorAll("[data-scroll-speed]").forEach((el) => {
        el.removeAttribute("data-scroll-speed");
      });

      /*
        ──────────────────────────────────────────────────────────
        4. Keep the caption‑width calc from the legacy code
        ──────────────────────────────────────────────────────────
      */
      const resizeCaptions = () => {
        this.querySelectorAll(".slide-image[data-caption]").forEach((img) => {
          const cap = img.querySelector(".slide-image__caption");
          if (cap) cap.style.width = img.offsetHeight - 10 + "px";
        });
      };
      resizeCaptions();
      window.addEventListener("resize", _debounce(resizeCaptions, 120));
    }
  }

  // Register (or overwrite) the <vertical-slider> custom element
  if (typeof customElements.get("vertical-slider") === "undefined") {
    customElements.define("vertical-slider", SliderVerticalStatic);
  } else {
    // If it was already defined by the old script, force‑redefine it
    try {
      customElements.define("vertical-slider", SliderVerticalStatic, {
        overrides: true,
      });
    } catch (_) {
      // browsers without `overrides` just ignore – page refresh picks up change
    }
  }
}
